import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, IconButton, InputLabel, Stack, TextField } from '@mui/material';
import { useAtom } from 'jotai';
import { isEmpty } from 'lodash';
import { useState } from 'react';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import useDraftState from '@/hooks/useDraftState';
import useStyles from '@/pages/Administration/AdministrationGeneral/AdministrationGeneral.styles';
import {
  type Organization,
  selectedOrganizationAtom,
  useFetchOrganizations,
  useMutationOrganizations
} from '@/pages/Organizations';

const OrganizationInformation = () => {
  const { classes } = useStyles();
  const [editing, isEditing] = useState<boolean>(false);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom); // selectedOrganization is specific organization information
  const [updatedOrgInfo, updateDraft, resetDraft] =
    useDraftState(selectedOrganization);
  const { refetch } = useFetchOrganizations();
  const { mutate } = useMutationOrganizations(refetch, selectedOrganization.id);

  const handleOrgInfoSave = () => {
    const trimmedName = updatedOrgInfo.name.trim();
    if (trimmedName !== '') {
      const newOrgInfo: Organization = {
        ...updatedOrgInfo,
        name: trimmedName
      };
      updateDraft({ name: trimmedName });
      mutate(newOrgInfo, { onError: () => resetDraft() }); // Mutation using trimmed name
      isEditing(!editing);
    }
  };

  const handleOrgInfoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateDraft({ name: event.target.value });
  };

  return (
    <LargeCard
      title="Organization Information"
      style={{ flex: 2 }}
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {editing ? (
            <Stack direction="row">
              <IconButton
                onClick={() => {
                  handleOrgInfoSave();
                }}
              >
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton
                onClick={() => {
                  isEditing(!editing);
                  resetDraft();
                }}
              >
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => isEditing(!editing)}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      <Grid container rowSpacing={0} columnSpacing={4}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <InputLabel id="organization_name">Organization Name</InputLabel>
          <TextField
            className={classes.textField}
            onChange={handleOrgInfoChange}
            fullWidth
            aria-labelledby="organization_name"
            name="organization_name"
            variant="outlined"
            disabled={!editing}
            value={updatedOrgInfo.name}
            error={isEmpty(updatedOrgInfo.name.trim())}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <InputLabel id="organization_label">Organization Label</InputLabel>
          <TextField
            className={classes.textField}
            fullWidth
            aria-labelledby="organization_label"
            name="organization_label"
            variant="outlined"
            disabled
            value={updatedOrgInfo.label}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <InputLabel id="uci">Universal Customer Identifier</InputLabel>
          <TextField
            className={classes.textField}
            fullWidth
            aria-labelledby="uci"
            name="uci"
            variant="outlined"
            disabled
            value={updatedOrgInfo.uci || ''}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <InputLabel id="industry">Industry</InputLabel>
          <TextField
            className={classes.textField}
            fullWidth
            aria-labelledby="industry"
            name="industry"
            variant="outlined"
            disabled
            value={selectedOrganization?.industry || 'No Industry Name'}
          />
        </Grid>
      </Grid>
    </LargeCard>
  );
};

export default OrganizationInformation;
